import { watch, Ref, ref, onMounted, nextTick, toRaw, computed, toRefs, onUpdated, shallowRef } from 'vue';
import * as echarts from 'echarts';

interface Props {
  rawData?: Charts.ChartsProps.RawData;
  colorList: Charts.ChartsProps.ColorItem[];
  height?: number | string;
  width?: number | string;
  responsiveWidth?: boolean;
  responsiveHeight?: boolean;
  eventType?: string;
  function?: Function;
}

export default function (props: Props) {
  const chart = shallowRef();
  const id = ref();

  const uid = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  id.value = uid();

  const resolveHeight = computed(() => {
    return props.responsiveHeight ? '100%' : typeof props.height === 'number' ? `${props.height}px` : props.height;
  });

  const resolveWidth = computed(() => {
    return props.responsiveWidth ? '100%' : typeof props.width === 'number' ? `${props.width}px` : props.width;
  });

  const legends = props.rawData?.data.map((item: any) => item.data);

  const series = props.rawData?.data.flatMap((item: any) => {
    return [
      {
        name: item.data,
        type: 'bar',
        stack: item.stack,
        yAxisIndex: 0,
        data: item.yaxis_bar,
        // ถ้าต้องการ tooltip แบบ custom สามารถเพิ่มได้
        // tooltip: {
        //   valueFormatter: (value) => `${value} บาท`,
        // },
      },
      {
        name: item.data,
        type: 'line',
        yAxisIndex: 1,
        data: item.yaxis_line,
        // tooltip: {
        //   valueFormatter: (value) => `${value} ครั้ง`,
        // },
      },
    ];
  });

  const colorList = props.colorList?.flatMap((item: any) => {
    return [item.hexColor];
  });

  const option = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#999',
        },
      },
      formatter: (params: any) => {
        const grouped: Record<string, { bar?: number; line?: number; color: string }> = {};

        params.forEach((item: any) => {
          const name = item.seriesName;
          if (!grouped[name]) {
            grouped[name] = { color: item.color };
          }

          if (item.seriesType === 'bar') {
            grouped[name].bar = item.value;
          } else if (item.seriesType === 'line') {
            grouped[name].line = item.value;
          }
        });

        const date = params[0]?.axisValue || '';
        let result = `<div style="margin-bottom: 4px;"><b>${date}</b></div>`;
        result += `<table style="width: 100%;">`;

        for (const name in grouped) {
          const { bar, line, color } = grouped[name];
          const barFormatted = bar != null ? bar.toLocaleString() : '-';
          const lineFormatted = line != null ? line.toLocaleString() : '-';

          result += `
            <tr>
              <td style="text-align:left; white-space: nowrap;">
                <span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>
                ${name}
              </td>
              <td style="text-align:right; white-space: nowrap;">
                ${barFormatted} / ${lineFormatted}
              </td>
            </tr>
          `;
        }

        result += `</table>`;
        return result;
      },
    },
    legend: {
      show: true,
      selectedMode: true,
      data: legends,
    },
    color: colorList,
    xAxis: [
      {
        type: 'category',
        data: props.rawData?.data[0].xaxis,
        axisPointer: {
          type: 'shadow',
        },
      },
    ],
    yAxis: [
      {
        type: 'value',
        name: props.rawData?.data[0].yaxis_label_bar,
        axisLabel: {
          formatter: '{value} ฿',
        },
      },
      {
        type: 'value',
        name: props.rawData?.data[0].yaxis_label_line,
      },
    ],
    series: series,
  };

  const init = () => {
    const dom = document.getElementById(String(id.value)) as HTMLElement;
    try {
      chart.value = echarts.init(dom);
      chart.value.setOption(toRaw(option));
      let initResize = false;

      setTimeout(() => {
        initResize = true;
        chart.value.resize();
      }, 1000);

      new ResizeObserver(() => {
        if (initResize) chart.value.resize();
      }).observe(dom);

      if (props.eventType) {
        chart.value.on(props.eventType, props.function);
      }
    } catch (e) {
      //  TODO: this exception will happen when using with vue <transition>
    }
  };

  onMounted(() => {
    init();
  });

  watch(
    () => option,
    () => {
      if (chart.value) chart.value.setOption(toRaw(option));
    },
    {
      deep: true,
      immediate: true,
    },
  );

  watch([() => props.height, () => props.width], () => {
    setTimeout(() => {
      chart.value.resize();
    }, 100);
  });

  return { id, resolveHeight, resolveWidth };
}
